interface ErrorStateProps {
  message: string;
  code?: string | null;
  title?: string;
  onRetry?: () => void;
  compact?: boolean;
  className?: string;
}

/**
 * The backend may simply not be running. That must never look like a hang:
 * every failed fetch lands here, with a reason and a retry button.
 */
export default function ErrorState({
  message,
  code,
  title = "Не вдалося завантажити дані",
  onRetry,
  compact = false,
  className = "",
}: ErrorStateProps) {
  return (
    // A failed panel is tinted, not outlined: the wash reads as "this region is
    // broken" at a glance, where a red rectangle border just adds one more box
    // to a page already made of boxes.
    <div
      role="alert"
      className={`flex flex-col items-center justify-center gap-2 rounded-card bg-danger/8 px-4 text-center ${
        compact ? "py-6" : "py-10"
      } ${className}`}
    >
      <span className="grid size-9 place-items-center rounded-full bg-danger/15 text-sm text-danger">
        !
      </span>
      <div className="text-sm font-medium text-fg">{title}</div>
      <div className="max-w-md text-xs leading-relaxed text-muted">{message}</div>
      {code && <div className="jv-num text-micro text-muted">код: {code}</div>}
      {onRetry && (
        <button type="button" onClick={onRetry} className="jv-btn mt-1">
          Спробувати ще раз
        </button>
      )}
    </div>
  );
}

/** One-line banner for a page that still has (stale) content to show. */
export function ErrorBanner({
  message,
  onRetry,
  retryLabel = "Оновити",
  className = "",
}: {
  message: string;
  onRetry?: () => void;
  /**
   * Not every retry is a refetch. When the banner reports a failed *action*,
   * «Оновити» reads as "reload the page" and hides what the button will
   * actually do, so the caller names it.
   */
  retryLabel?: string;
  className?: string;
}) {
  return (
    <div
      role="alert"
      className={`flex flex-wrap items-center gap-3 rounded-card bg-danger/8 px-4 py-2.5 text-xs text-fg ${className}`}
    >
      <span className="shrink-0 font-medium text-danger">Помилка</span>
      <span className="min-w-0 flex-1 leading-relaxed text-muted">{message}</span>
      {onRetry && (
        <button type="button" onClick={onRetry} className="jv-btn shrink-0">
          {retryLabel}
        </button>
      )}
    </div>
  );
}
